import { useCallback, useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { useGetVehicleDocuments } from '../../hooks/backend/fleet'
import { useGetDocumentsSyncedAt } from '../../hooks/backend/shared'
import { useCapabilities } from '../../hooks/useCapabilities'
import { DocumentsBody } from '../documents/DocumentsBody'
import { DocumentPackDownload } from '../documents/DocumentPackDownload'
import type { UploadCategory } from '../documents/UploadControl'
import type { DocumentRow } from '../../utils/documents'
import type { FleetRow } from '../../utils/fleet'

const VEHICLE_CATEGORIES: UploadCategory[] = [
  { code: 'registration_licensing', label: 'Registration & licensing' },
  { code: 'service', label: 'Service invoices & receipts' },
  { code: 'repairs', label: 'Repairs invoices & receipts' },
  { code: 'insurance', label: 'Insurance claims' },
  { code: 'traffic_fines', label: 'Traffic fines & penalties' },
  { code: 'other', label: 'Other' },
]

export function VehicleDocumentsDialog({ vehicle }: { vehicle: FleetRow }) {
  const [open, setOpen] = useState(false)
  const docs = useGetVehicleDocuments()
  const synced = useGetDocumentsSyncedAt()
  const { canView, canEditFleet, isAdmin } = useCapabilities()

  const refetch = useCallback(() => {
    docs.trigger({ vehicle_id: vehicle.vehicle_id }, { skipCache: true })
    synced.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle.vehicle_id])

  // Fetch fresh on each open.
  useEffect(() => {
    if (!open) return
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vehicle.vehicle_id])

  const rows = (docs.data as DocumentRow[] | undefined) ?? []
  const syncState = synced.data as
    | { last_synced: string | null; is_stale: boolean | null }
    | undefined
  const syncedAt = syncState?.last_synced ?? null
  const syncStale = syncState?.is_stale ?? null

  const descriptor = [vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Unknown vehicle'

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
          <FileText className="h-3.5 w-3.5" />
          Documents
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-mono uppercase tracking-wide">
            {vehicle.registration_number || 'Vehicle'}
          </DialogTitle>
          <DialogDescription>{descriptor}</DialogDescription>
        </DialogHeader>
        <DocumentsBody
          rows={rows}
          loading={docs.loading}
          error={docs.error}
          emptyText="No documents cataloged for this vehicle yet."
          syncedAt={syncedAt}
          syncStale={syncStale}
          categories={VEHICLE_CATEGORIES}
          uploadScope={{ vehicle_id: vehicle.vehicle_id }}
          canUpload={canView}
          canDelete={isAdmin || canEditFleet}
          attachesTo="vehicle"
          onChanged={refetch}
        />
        {/* The statement evidence pack for this one car. Same component the
            owner dialog uses, scoped by vehicle instead of by person. */}
        <DocumentPackDownload
          scope={{ vehicle_id: vehicle.vehicle_id }}
          scopeLabel={vehicle.registration_number || 'Vehicle'}
        />
      </DialogContent>
    </Dialog>
  )
}
